import { onMounted, onUnmounted, Ref, watch } from "vue";

/**
 * Polling intervals in milliseconds
 */
export const AGGRESSIVE_POLL_INTERVAL = 1000; // 1 second
export const UNSTABLE_POLL_INTERVAL = 15000; // 15 seconds
export const STABLE_POLL_INTERVAL = 30000; // 30 seconds
export const AGGRESSIVE_PHASE_DURATION = 30000; // 30 seconds
export const EVENT_DEBOUNCE_MS = 500; // Debounce for event-triggered fetches

/**
 * Extended Navigator interface to include Network Information API
 * @see https://developer.mozilla.org/en-US/docs/Web/API/NetworkInformation
 */
interface NetworkInformation extends EventTarget {
  readonly downlink?: number;
  readonly effectiveType?: "slow-2g" | "2g" | "3g" | "4g";
  readonly rtt?: number;
  readonly saveData?: boolean;
  readonly type?: string;
  onchange?: EventListener;
}

interface NavigatorWithConnection extends Navigator {
  readonly connection?: NetworkInformation;
  readonly mozConnection?: NetworkInformation;
  readonly webkitConnection?: NetworkInformation;
}

/**
 * Gets the Network Information API connection object if available
 * Handles vendor prefixes for cross-browser support
 */
export function getNetworkConnection(): NetworkInformation | undefined {
  const nav = navigator as NavigatorWithConnection;
  return nav.connection || nav.mozConnection || nav.webkitConnection;
}

interface UseStatusPollingOptions {
  /**
   * Function to fetch the current status
   */
  fetchStatus: () => Promise<void>;

  /**
   * Reactive reference indicating if there are unstable ACLs
   */
  hasUnstableAcls: Ref<boolean>;

  /**
   * Whether to automatically start polling on mount (default: true)
   */
  autoStart?: boolean;
}

interface UseStatusPollingReturn {
  /**
   * Triggers aggressive polling for 30 seconds.
   * Call this after grant/revoke actions.
   */
  triggerAggressivePolling: () => void;

  /**
   * Manually starts the polling intervals.
   * Only needed if autoStart is false.
   */
  start: () => void;

  /**
   * Stops all polling intervals and removes event listeners.
   */
  stop: () => void;
}

/**
 * Composable for polling status with three-tier intervals:
 * - Aggressive (1s): For 30 seconds when there are unstable ACLs
 * - Medium (15s): When there are unstable ACLs but aggressive phase ended
 * - Background (30s): When all ACLs are stable
 *
 * Also polls immediately on:
 * - Window focus
 * - Browser coming back online
 * - Network connection changes (Network Information API, where supported)
 *
 * Event-triggered fetches are debounced (500ms) to prevent multiple rapid API calls
 * when multiple events fire simultaneously (e.g., reconnecting can trigger online,
 * focus, and network change events at once).
 *
 * @example
 * ```ts
 * const { triggerAggressivePolling } = useStatusPolling({
 *   fetchStatus: () => authStore.fetchStatus({ force: true }),
 *   hasUnstableAcls: computed(() => !user.value?.ACLs?.every(isGrantInStableState)),
 * });
 *
 * // After a grant/revoke action:
 * triggerAggressivePolling();
 * ```
 */
export function useStatusPolling(options: UseStatusPollingOptions): UseStatusPollingReturn {
  const { fetchStatus, hasUnstableAcls, autoStart = true } = options;

  let statusInterval: ReturnType<typeof setInterval> | null = null;
  let pollCount = 0;
  let aggressivePollUntil = 0;
  let isStarted = false;
  let lastEventFetchTime = 0;

  /**
   * Triggers aggressive polling for 30 seconds
   */
  function triggerAggressivePolling(): void {
    aggressivePollUntil = Date.now() + AGGRESSIVE_PHASE_DURATION;
  }

  /**
   * Debounced fetch to prevent multiple rapid API calls.
   * Used by all fetch triggers (events and interval polling) to prevent
   * redundant calls when multiple triggers fire close together.
   */
  async function debouncedFetch(): Promise<void> {
    const now = Date.now();
    if (now - lastEventFetchTime < EVENT_DEBOUNCE_MS) {
      return;
    }
    lastEventFetchTime = now;
    await fetchStatus();
  }

  /**
   * Handles the window focus event by fetching status
   */
  async function handleWindowFocus(): Promise<void> {
    await debouncedFetch();
  }

  /**
   * Handles the browser coming back online
   * Fetches status - if there are unstable ACLs, the watcher will trigger aggressive polling
   */
  async function handleOnline(): Promise<void> {
    await debouncedFetch();
  }

  /**
   * Handles network connection changes (Network Information API)
   * Triggers a status update when the connection type changes
   */
  async function handleNetworkChange(): Promise<void> {
    await debouncedFetch();
  }

  /**
   * Starts the polling interval and window focus listener
   */
  function start(): void {
    if (isStarted) {
      return;
    }
    isStarted = true;

    // Start aggressive polling if there are unstable ACLs on start
    if (hasUnstableAcls.value) {
      triggerAggressivePolling();
    }

    pollCount = 0;
    statusInterval = setInterval(async () => {
      pollCount++;

      const now = Date.now();
      const inAggressivePhase = now < aggressivePollUntil;

      try {
        if (hasUnstableAcls.value) {
          if (inAggressivePhase) {
            // Aggressive phase: poll every 1 second for 30 seconds
            await debouncedFetch();
          } else if (pollCount % (UNSTABLE_POLL_INTERVAL / AGGRESSIVE_POLL_INTERVAL) === 0) {
            // Non-aggressive but unstable: poll every 15 seconds
            await debouncedFetch();
          }
        } else if (pollCount % (STABLE_POLL_INTERVAL / AGGRESSIVE_POLL_INTERVAL) === 0) {
          // Stable: poll every 30 seconds
          await debouncedFetch();
        }
      } catch {
        // Errors during status polling are silently ignored to prevent
        // breaking the polling loop. The caller should handle errors
        // in their fetchStatus implementation if needed.
      }
    }, AGGRESSIVE_POLL_INTERVAL);

    // Poll on window focus
    window.addEventListener("focus", handleWindowFocus);

    // Poll when browser comes back online
    window.addEventListener("online", handleOnline);

    // Poll on network connection changes (Network Information API)
    const connection = getNetworkConnection();
    if (connection) {
      connection.addEventListener("change", handleNetworkChange);
    }
  }

  /**
   * Stops polling and removes event listeners
   */
  function stop(): void {
    if (statusInterval) {
      clearInterval(statusInterval);
      statusInterval = null;
    }
    window.removeEventListener("focus", handleWindowFocus);
    window.removeEventListener("online", handleOnline);

    const connection = getNetworkConnection();
    if (connection) {
      connection.removeEventListener("change", handleNetworkChange);
    }

    isStarted = false;
  }

  // Watch for changes to hasUnstableAcls to trigger aggressive polling
  watch(hasUnstableAcls, (isUnstable, wasUnstable) => {
    if (isUnstable && !wasUnstable) {
      // ACLs became unstable, trigger aggressive polling
      triggerAggressivePolling();
    }
  });

  if (autoStart) {
    onMounted(() => {
      start();
    });

    onUnmounted(() => {
      stop();
    });
  }

  return {
    triggerAggressivePolling,
    start,
    stop,
  };
}
